import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Row, Col, ListGroup, Image, Card, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { PayPalButtons } from '@paypal/react-paypal-js';
import GooglePayButton from '@google-pay/button-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { BookProgressBar } from '../components/BookProgressBar';
import { getOrderDetails } from '../actions/orderActions';
import { getMyGenaiJobs } from '../actions/genaiActions';
import { ORDER_PAY_RESET } from '../constants/orderConstants';
import { BOOK_PRODUCT_ID, LANGUAGE_ADDON_PRODUCT_ID, PRINT_PRODUCT_ID } from '../constants/productConfig';
import { detectMarket, getCurrencyForMarket } from '../utils/market';

function FreeOrderButton({ orderId, onComplete }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { userInfo } = useSelector(state => state.userLogin);

    const handleComplete = async () => {
        setLoading(true);
        setError('');
        try {
            await axios.post(
                `/api/payments/free-order/${orderId}/complete/`,
                {},
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            );
            onComplete();
        } catch (err) {
            setError(err.response?.data?.error || t('order.orderFailed'));
            setLoading(false);
        }
    };

    return (
        <div>
            {error && <Message variant='danger'>{error}</Message>}
            <Button className='w-100' onClick={handleComplete} disabled={loading}>
                {loading ? t('order.processing') : t('order.completeFreeOrder')}
            </Button>
        </div>
    );
}

function OrderScreen() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [sdkReady, setSdkReady] = useState(false);
    const [paypalError, setPaypalError] = useState('');
    const [downloadingJobId, setDownloadingJobId] = useState(null);

    const handleDownloadPDF = async (jobId) => {
        setDownloadingJobId(jobId);
        try {
            const response = await fetch(`/api/genai/jobs/${jobId}/download-pdf/`, {
                headers: { Authorization: `Bearer ${userInfo.token}` },
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                alert(err.error || t('order.pdfFailed'));
                return;
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const cd = response.headers.get('Content-Disposition');
            const match = cd && cd.match(/filename="(.+)"/);
            link.download = match ? match[1] : `book_${jobId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            alert(t('order.pdfFailed'));
        } finally {
            setDownloadingJobId(null);
        }
    };

    const orderDetails = useSelector(state => state.orderDetails);
    const { order, error, loading } = orderDetails;

    const orderPay = useSelector(state => state.orderPay);
    const { loading: loadingPay, success: successPay } = orderPay;

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const genaiMyJobs = useSelector(state => state.genaiMyJobs);
    const { jobs: allJobs } = genaiMyJobs || {};

    const formatShippingAddress = (addressObj = {}) => {
        const addressLine = addressObj.address || addressObj.Address || '';
        const city = addressObj.city || addressObj.City || '';
        const postalCode = addressObj.postalCode || addressObj.postal_code || addressObj.postal || '';
        const country = addressObj.country || addressObj.Country || '';

        const cityPostal = [city, postalCode].filter(Boolean).join(' ');
        return [addressLine, cityPostal, country].filter(Boolean).join(', ');
    };

    // Build a map: order_item_id → job (most recent job per item)
    const jobsByItemId = React.useMemo(() => {
        const map = {};
        if (!allJobs || !order) return map;
        const orderJobs = allJobs.filter(j => Number(j.order_id) === Number(order.OrderId));
        orderJobs.forEach(j => {
            const itemId = j.order_item_id;
            if (!itemId) {
                // No item id — link to first untracked item
                if (!map['__any__'] || new Date(j.updated_at) > new Date(map['__any__'].updated_at)) {
                    map['__any__'] = j;
                }
            } else {
                if (!map[itemId] || new Date(j.updated_at) > new Date(map[itemId].updated_at)) {
                    map[itemId] = j;
                }
            }
        });
        return map;
    }, [allJobs, order]);

    // Items to show book action buttons for:
    // 1. Items matching BOOK_PRODUCT_ID, OR
    // 2. Items that already have a genai job (covers orders placed before productConfig was set)
    // 3. Fallback: if __any__ job exists and nothing matched, use first non-addon item
    const bookOrderItems = React.useMemo(() => {
        if (!order?.orderItems) return [];
        const matched = order.orderItems.filter(item =>
            Number(item.ProductId) === BOOK_PRODUCT_ID ||
            jobsByItemId[item.OrderItemId] !== undefined
        );
        if (matched.length === 0 && jobsByItemId['__any__']) {
            const fallback = order.orderItems.find(item =>
                Number(item.ProductId) !== LANGUAGE_ADDON_PRODUCT_ID &&
                Number(item.ProductId) !== PRINT_PRODUCT_ID
            );
            return fallback ? [fallback] : [];
        }
        return matched;
    }, [order, jobsByItemId]);

    if (!loading && !error && order?.orderItems) {
        order.itemsPrice = order.orderItems.reduce((acc, item) => acc + parseFloat(item.price) * item.qty, 0).toFixed(2);
    }

    useEffect(() => {
        if (!userInfo) {
            navigate('/login');
            return;
        }

        if (!order || successPay || order.OrderId !== Number(id)) {
            dispatch({ type: ORDER_PAY_RESET });
            dispatch(getOrderDetails(id));
        } else if (!order.is_paid) {
            setSdkReady(true);
        }

        if (order && order.is_paid) {
            dispatch(getMyGenaiJobs());
        }
    }, [dispatch, order, id, successPay, userInfo, navigate]);

    const refreshOrder = async () => {
        dispatch({ type: ORDER_PAY_RESET });
        dispatch(getOrderDetails(id));
    };

    const createPayPalOrder = async () => {
        setPaypalError('');
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            const { data } = await axios.post(
                `/api/payments/paypal/orders/${id}/create/`,
                {},
                config
            );

            if (!data?.id) {
                throw new Error('PayPal order id missing in response');
            }

            return data.id;
        } catch (err) {
            const errorMessage = err.response?.data?.error || t('order.paypalStartFailed');
            setPaypalError(errorMessage);
            throw err;
        }
    };

    const capturePayPalOrder = async (orderID) => {
        setPaypalError('');
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            await axios.post(
                `/api/payments/paypal/orders/${id}/capture/`,
                { orderID },
                config
            );

            await refreshOrder();
        } catch (err) {
            const errorMessage = err.response?.data?.error || t('order.paypalCaptureFailed');
            setPaypalError(errorMessage);
            throw err;
        }
    };

    const resolveJobProgressRoute = (job) => {
        const step = job?.current_step || '';
        const status = job?.status || '';
        const jobId = job?.job_id;

        if (!jobId) {
            return null;
        }

        if (
            status === 'completed' ||
            status === 'assembling' ||
            step === 'I_assemble' ||
            step === 'J_approve_final'
        ) {
            return `/genai/job/${jobId}/final-review`;
        }

        if (
            step === 'E_generate_cover' ||
            step === 'E1_generate_cover' ||
            step === 'E2_translate_cover' ||
            step === 'F_approve_cover'
        ) {
            return `/genai/job/${jobId}/review-pages`;
        }

        if (
            status === 'images_qa' ||
            step === 'G_generate_pages' ||
            step === 'G1_generate_pages' ||
            step === 'G2_translate_back' ||
            step === 'H_approve_pages'
        ) {
            return `/genai/job/${jobId}/review-pages`;
        }

        if (
            status === 'images_generating' &&
            (step === 'G_generate_pages' || step === 'G1_generate_pages' || step === 'H_approve_pages')
        ) {
            return `/genai/job/${jobId}/review-pages`;
        }

        if (
            status === 'images_generating' &&
            (step === 'E_generate_cover' || step === 'E1_generate_cover' || step === 'F_approve_cover')
        ) {
            return `/genai/job/${jobId}/review-pages`;
        }

        return `/genai/job/${jobId}/review-story`;
    };

    const handleCreateOrResumeBook = async (item) => {
        // Detect language add-on in the order
        const languageAddonItem = order.orderItems.find(
            i => Number(i.ProductId) === LANGUAGE_ADDON_PRODUCT_ID
        );
        const hasLanguageAddon = Boolean(languageAddonItem);
        // Extract the chosen language from add-on item name, e.g. "Non-English Book (Danish)" → "Danish"
        const langMatch = languageAddonItem?.name?.match(/\(([^)]+)\)/);
        const addonLanguage = langMatch ? langMatch[1] : '';
        const createStoryUrl = `/genai/create_story?order_id=${order.OrderId}&order_item_id=${item.OrderItemId}`
            + (hasLanguageAddon ? '' : '&english_only=1')
            + (addonLanguage ? `&language=${encodeURIComponent(addonLanguage)}` : '');

        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            const { data: jobs } = await axios.get('/api/genai/jobs/', config);
            const matchingJobs = (jobs || []).filter((job) => (
                Number(job?.order_id) === Number(order.OrderId) &&
                Number(job?.order_item_id) === Number(item.OrderItemId)
            ));

            if (matchingJobs.length > 0) {
                const latestJob = matchingJobs.sort((a, b) => (
                    new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
                ))[0];

                const progressRoute = resolveJobProgressRoute(latestJob);
                if (progressRoute) {
                    navigate(progressRoute);
                    return;
                }
            }

            navigate(createStoryUrl);
        } catch (resumeError) {
            navigate(createStoryUrl);
        }
    };

    const hasPrintedBookItems = !loading && !error && Boolean(order?.orderItems?.some((item) => (
        Number(item.ProductId) === BOOK_PRODUCT_ID || Number(item.ProductId) === PRINT_PRODUCT_ID
    )));
    const isAllDigital = !loading && !error && !hasPrintedBookItems && Boolean(order?.is_all_digital);

    return loading ? (
        <Loader />
    ) : error ? (
        <Message variant='danger'>{error}</Message>
    ) : (
        <div>
            <h1>{t('order.title', { id: order.OrderId })}</h1>
            <Row>
                <Col md={8}>
                    <ListGroup variant='flush'>
                        <ListGroup.Item>
                            <h2>{t('order.orderItems')}</h2>
                            {order.orderItems.length === 0 ? (
                                <Message variant='info'>{t('order.empty')}</Message>
                            ) : (
                                <ListGroup variant='flush'>
                                    {order.orderItems.map((item, index) => (
                                        <ListGroup.Item key={index}>
                                            <Row>
                                                <Col md={1}>
                                                    <Image src={item.image} alt={item.name} fluid rounded />
                                                </Col>
                                                <Col>
                                                    <Link to={`/product/${item.ProductId}`}>
                                                        {item.name}
                                                    </Link>
                                                    {order.is_paid && (
                                                            Number(item.ProductId) === BOOK_PRODUCT_ID ||
                                                            jobsByItemId[item.OrderItemId] ||
                                                            jobsByItemId['__any__']
                                                        ) && (
                                                        <div className="mt-2">
                                                            <BookProgressBar
                                                                job={jobsByItemId[item.OrderItemId] || jobsByItemId['__any__'] || null}
                                                                order={order}
                                                                compact={false}
                                                            />
                                                        </div>
                                                    )}
                                                </Col>
                                                <Col md={4}>
                                                    {item.qty} x {item.price} {t('common.dkk')} = {(item.qty * item.price).toFixed(2)} {t('common.dkk')}
                                                </Col>
                                            </Row>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </ListGroup.Item>

                        <ListGroup.Item>
                            <h2>{t('order.shipping')}</h2>
                            <p><strong>{t('order.name')}</strong> {order.user.name}</p>
                            <p><strong>{t('order.email')}</strong><a href={`mailto:${order.user.email}`}>{order.user.email}</a></p>
                            {order.shippingAddress?.phone_number && (
                                <p><strong>{t('order.phone')}</strong> {order.shippingAddress.phone_number}</p>
                            )}
                            <p>
                                <strong>{t('order.shippingLabel')}</strong>
                                {isAllDigital ? t('order.digitalDelivery') : (
                                    (formatShippingAddress(order.shippingAddress) || '-')
                                )}
                            </p>

                            {(isAllDigital || order.is_delivered) ? (
                                <Message variant='success'>
                                    {isAllDigital ? t('order.digitallyDelivered') : t('order.deliveredOn', { date: order.delivered_at })}
                                </Message>
                            ) : (
                                <Message variant='warning'>{t('order.notDelivered')}</Message>
                            )}
                        </ListGroup.Item>

                        <ListGroup.Item>
                            <h2>{t('order.paymentMethod')}</h2>
                            <p>
                                <strong>{t('order.method')}</strong>
                                {order.payment_method}
                            </p>
                            {order.is_paid ? (
                                <Message variant='success'>{t('order.paidOn', { date: order.paid_at })}</Message>
                            ) : (
                                <Message variant='warning'>{t('order.notPaid')}</Message>
                            )}
                        </ListGroup.Item>
                    </ListGroup>
                </Col>

                <Col md={4}>
                    <Card>
                        <ListGroup variant='flush'>
                            <ListGroup.Item>
                                <h2>{t('order.orderSummary')}</h2>
                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Row>
                                    <Col>{t('order.items')}</Col>
                                    <Col>{order.itemsPrice} {t('common.dkk')}</Col>
                                </Row>
                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Row>
                                    <Col>{t('order.shippingCost')}</Col>
                                    <Col>{order.shipping_price} {t('common.dkk')}</Col>
                                </Row>
                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Row>
                                    <Col>{t('order.tax')}</Col>
                                    <Col>{order.tax_price} {t('common.dkk')}</Col>
                                </Row>
                            </ListGroup.Item>

                            {order.discount_amount > 0 && (
                                <ListGroup.Item>
                                    <Row>
                                        <Col>{t('order.discount', { code: order.voucher_code })}</Col>
                                        <Col className='text-success'>-{Number(order.discount_amount).toFixed(2)} {t('common.dkk')}</Col>
                                    </Row>
                                </ListGroup.Item>
                            )}

                            <ListGroup.Item>
                                <Row>
                                    <Col>{t('order.total')}</Col>
                                    <Col>{order.total_price} {t('common.dkk')}</Col>
                                </Row>
                            </ListGroup.Item>

                            {!order.is_paid && (
                                <ListGroup.Item>
                                    {loadingPay && <Loader />}

                                    {Number(order.total_price) <= 0 ? (
                                        <FreeOrderButton orderId={order.OrderId} onComplete={refreshOrder} />
                                    ) : !sdkReady ? (
                                        <Loader />
                                    ) : (
                                        <div>
                                            {paypalError && <Message variant='danger'>{paypalError}</Message>}
                                            {order.payment_method === 'PayPal' && (
                                                <PayPalButtons
                                                    style={{ layout: 'vertical', shape: 'rect', label: 'paypal' }}
                                                    createOrder={() => createPayPalOrder()}
                                                    onApprove={(data) => capturePayPalOrder(data.orderID)}
                                                    onError={(err) => {
                                                        console.error('PayPal button error:', err);
                                                        setPaypalError(t('order.paypalFailed'));
                                                    }}
                                                />
                                            )}

                                            {order.payment_method === 'GooglePay' && (
                                                <GooglePayButton
                                                    environment='TEST'
                                                    buttonSizeMode='fill'
                                                    paymentRequest={{
                                                        apiVersion: 2,
                                                        apiVersionMinor: 0,
                                                        allowedPaymentMethods: [
                                                            {
                                                                type: 'CARD',
                                                                parameters: {
                                                                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                                                                    allowedCardNetworks: ['MASTERCARD', 'VISA'],
                                                                },
                                                                tokenizationSpecification: {
                                                                    type: 'PAYMENT_GATEWAY',
                                                                    parameters: {
                                                                        gateway: 'example',
                                                                        gatewayMerchantId: 'exampleGatewayMerchantId',
                                                                    },
                                                                },
                                                            },
                                                        ],
                                                        merchantInfo: {
                                                            merchantId: '12345678901234567890',
                                                            merchantName: 'TaleTots',
                                                        },
                                                        transactionInfo: {
                                                            totalPriceStatus: 'FINAL',
                                                            totalPriceLabel: 'Total',
                                                            totalPrice: order.total_price.toString(),
                                                            currencyCode: order.customization?.pricing?.currency || getCurrencyForMarket(detectMarket()),
                                                            countryCode: order.customization?.pricing?.market === 'de' ? 'DE' : order.customization?.pricing?.market === 'se' ? 'SE' : order.customization?.pricing?.market === 'no' ? 'NO' : 'DK',
                                                        },
                                                    }}
                                                    onLoadPaymentData={paymentRequest => {
                                                        console.log('Google Pay payment data:', paymentRequest);
                                                    }}
                                                    onError={gpError => {
                                                        console.error('Google Pay error:', gpError);
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </ListGroup.Item>
                            )}
                        </ListGroup>

                        {order.is_paid && (
                            <>
                                <ListGroup.Item>
                                    <Message variant='success'>
                                        {t('order.paymentSuccess')}
                                    </Message>
                                </ListGroup.Item>

                                {order.orderItems && bookOrderItems
                                    .map((item, index, arr) => (
                                    <ListGroup.Item key={item.OrderItemId || index}>
                                        <div className='d-flex align-items-center justify-content-between mb-2'>
                                            <div className='d-flex align-items-center'>
                                                <Image
                                                    src={item.image}
                                                    alt={item.name}
                                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                    rounded
                                                />
                                                <span className='ms-3'>{item.name}</span>
                                            </div>
                                        </div>
                                        {(() => {
                                            const job = jobsByItemId[item.OrderItemId] || jobsByItemId['__any__'];
                                            const isComplete = job && (job.completed_at || job.status === 'completed');
                                            const suffix = arr.length > 1 ? ` #${index + 1}` : '';
                                            if (isComplete) {
                                                return (
                                                    <>
                                                        <Button
                                                            className='w-100 mb-2'
                                                            variant='success'
                                                            onClick={() => navigate(`/genai/job/${job.job_id}/final-review`)}
                                                        >
                                                            <i className='fas fa-book-open me-2'></i>
                                                            {suffix ? t('order.viewBookN', { n: index + 1 }) : t('order.viewBook')}
                                                        </Button>
                                                        {job.pdf_url && (
                                                            <Button
                                                                className='w-100'
                                                                variant='outline-success'
                                                                onClick={() => handleDownloadPDF(job.job_id)}
                                                                disabled={downloadingJobId === job.job_id}
                                                            >
                                                                <i className='fas fa-download me-2'></i>
                                                                {downloadingJobId === job.job_id ? t('order.downloading') : (suffix ? t('order.downloadPdfN', { n: index + 1 }) : t('order.downloadPdf'))}
                                                            </Button>
                                                        )}
                                                    </>
                                                );
                                            }
                                            return (
                                                <Button
                                                    className='w-100'
                                                    variant='primary'
                                                    onClick={() => handleCreateOrResumeBook(item)}
                                                >
                                                    <i className='fas fa-book-open me-2'></i>
                                                    {job
                                                        ? (suffix ? t('order.continueCreatingN', { n: index + 1 }) : t('order.continueCreating'))
                                                        : (suffix ? t('order.createBookN', { n: index + 1 }) : t('order.createBook'))
                                                    }
                                                </Button>
                                            );
                                        })()}
                                    </ListGroup.Item>
                                ))}

                                <ListGroup.Item>
                                    <Link to='/products'>
                                        <Button className='w-100' variant='outline-secondary'>{t('order.continueShopping')}</Button>
                                    </Link>
                                </ListGroup.Item>
                            </>
                        )}

                        {loadingPay && <Loader />}
                        {userInfo && userInfo.isAdmin && order.is_paid && !order.is_delivered && (
                            <ListGroup.Item>
                                <Button
                                    type='button'
                                    className='w-100'
                                    onClick={async () => {
                                        if (window.confirm(t('order.markDelivered'))) {
                                            try {
                                                const config = {
                                                    headers: {
                                                        Authorization: `Bearer ${userInfo.token}`,
                                                    },
                                                };
                                                await axios.put(`/api/orders/${id}/deliver/`, {}, config);
                                                dispatch(getOrderDetails(id));
                                            } catch (err) {
                                                alert(err.response?.data?.detail || err.message);
                                            }
                                        }
                                    }}
                                >
                                    {t('order.markAsDelivered')}
                                </Button>
                            </ListGroup.Item>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default OrderScreen;
